import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  NavigateOptions,
  To,
  useLocation, // eslint-disable-next-line custom-rules/no-use-navigate
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import UrlUtils from '@utils/url'

/**
 * Props for navigating to a specific path
 * @interface INavigateToProps
 */
export interface INavigateToProps {
  /** The path to navigate to */
  path: To
  /** Whether to replace the current history entry instead of adding a new one */
  replace?: NavigateOptions['replace']
  /** State to pass with the navigation */
  state?: NavigateOptions['state']
  /** Whether to trim the last segment of the current path and append the new path */
  trimLastPath?: boolean
}

/**
 * Props for navigating back in history
 * @interface INavigateBackProps
 */
interface INavigateBackProps {
  /** Path to navigate to if there's no previous history */
  fallbackPath: string
  /** Whether to ignore query parameters from the previous URL */
  ignorePreviousUrlQueryParams?: boolean
  /** State to pass with the navigation */
  state?: NavigateOptions['state']
}

/**
 * Props for changing URL search parameters
 * @interface IChangeUrlSearchParamsProps
 */
interface IChangeUrlSearchParamsProps {
  /** Parameters to set - can be a Record or URLSearchParams */
  params: Record<string, string> | URLSearchParams
  /** Whether to replace the current history entry */
  replace?: boolean
  /** Whether to preserve the hash fragment when updating search params */
  preserveHash?: boolean
}

/**
 * Props for changing the URL path
 * @interface IChangeUrlPathProps
 */
interface IChangeUrlPathProps {
  /** The new path to set */
  newPath: string
  /** Whether to replace the current history entry (default: true) */
  replace?: boolean
}

/**
 * Props for changing the URL hash
 * @interface IChangeHashProps
 */
interface IChangeHashProps {
  /** The new hash to set (without the # symbol) */
  newHash: string
  /** Whether to replace the current history entry (default: true) */
  replace?: boolean
}

/**
 * Navigation context interface providing navigation utilities
 * @interface NavigationContextProps
 */
interface NavigationContextProps {
  /** Read-only array of navigation history paths */
  readonly history: readonly string[]
  /** Navigate to a specific path */
  navigateTo: ({ path, replace, state }: INavigateToProps) => void
  /** Navigate back in history with fallback */
  navigateBack: ({
    fallbackPath,
    state,
    ignorePreviousUrlQueryParams,
  }: INavigateBackProps) => void
  /** Change URL search parameters */
  changeUrlSearchParams: ({
    params,
    replace,
  }: IChangeUrlSearchParamsProps) => void
  /** Change the entire URL path */
  changeUrlPath: ({ newPath, replace }: IChangeUrlPathProps) => void
  /** Change only the URL hash */
  changeHash: ({ newHash, replace }: IChangeHashProps) => void
}

export const NavigationContext = createContext<
  NavigationContextProps | undefined
>(undefined)

interface NavigationProviderProps {
  children: ReactNode
  initialHistory?: string[]
}

const NavigationProvider = memo(
  ({ children, initialHistory = [] }: NavigationProviderProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [_, setSearchParams] = useSearchParams()
    const [history, setHistory] = useState<string[]>(initialHistory)

    const lockPopHistory = useRef(false)

    /**
     * Parameters for checking if the path should be updated
     * @interface IShouldUpdatePathParams
     */
    interface IShouldUpdatePathParams {
      /** The new path to check */
      newPath: string
      /** The current history array */
      currentHistory: string[]
      /** Whether this is a replace operation */
      replace: boolean
    }

    /**
     * Determines if the path should be updated in history
     * Prevents duplicate consecutive entries in history
     * @param params - The parameters for checking
     * @returns Whether the path should be updated
     */
    const shouldUpdatePath = useCallback(
      ({ newPath, currentHistory, replace }: IShouldUpdatePathParams) => {
        const LAST_ITEM_INDEX = 1
        const SECOND_TO_LAST_ITEM_INDEX = 2

        const lastPath =
          currentHistory[
            currentHistory.length -
              (replace ? SECOND_TO_LAST_ITEM_INDEX : LAST_ITEM_INDEX)
          ]
        return lastPath !== newPath
      },
      []
    )

    useEffect(() => {
      const { pathname, search, hash } = location
      const fullPath = `${pathname}${search ?? ''}${hash ?? ''}`
      const isHistoryEmpty = history.length === 0

      if (
        isHistoryEmpty ||
        shouldUpdatePath({
          newPath: fullPath,
          currentHistory: history,
          replace: false,
        })
      ) {
        setHistory((prev) => [...prev, fullPath])
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, shouldUpdatePath])

    /**
     * Removes the last entry from the navigation history
     * Uses a lock mechanism to prevent race conditions
     */
    const popHistory = useCallback(() => {
      if (lockPopHistory.current) {
        return
      }

      lockPopHistory.current = true
      setHistory((prevHistory) => prevHistory.slice(0, -1))

      setTimeout(() => {
        lockPopHistory.current = false
      }, 100)
    }, [])

    /**
     * Navigates to a specific path.
     * @param params
     *   path: The path to navigate to. If trimLastPath is true, this is appended to the current path minus its last segment.
     *   replace: If true, replaces the current history entry instead of adding a new one.
     *   state: Optional state to pass with the navigation.
     *   trimLastPath: If true, replaces the last segment of the current path with the new path segment.
     */
    const navigateTo = useCallback(
      ({ path, replace, state, trimLastPath }: INavigateToProps) => {
        const options: NavigateOptions = {}

        if (trimLastPath) {
          const currentPath = location.pathname
          const basePath = currentPath.split('/').slice(0, -1).join('/')
          path = `${basePath}/${path}`.replace(/\/+/g, '/')
        }

        if (replace) {
          popHistory()
          options.replace = true
        }

        if (state) {
          options.state = state
        }

        navigate(path, ...(Object.keys(options).length > 0 ? [options] : []))
      },
      [location.pathname, navigate, popHistory]
    )

    const navigateBackRef = useRef<
      ({
        fallbackPath,
        state,
        ignorePreviousUrlQueryParams,
      }: INavigateBackProps) => void
    >(() => {})

    useEffect(() => {
      navigateBackRef.current = ({
        fallbackPath,
        state,
        ignorePreviousUrlQueryParams,
      }: INavigateBackProps) => {
        let path: string | undefined

        if (history.length > 1) {
          path = history[history.length - 2]
        } else if (fallbackPath) {
          path = fallbackPath
        }

        if (path) {
          if (ignorePreviousUrlQueryParams) {
            path = path.split('?')[0]
          }

          popHistory()
          navigate(path, { replace: true, state })
        }
      }
    }, [history, navigate, popHistory])

    /**
     * Navigates back to the previous page in history.
     * @param params
     *   fallbackPath: Path to navigate to if there is no previous history.
     *   ignorePreviousUrlQueryParams: If true, removes query parameters from the previous URL when navigating back.
     *   state: Optional state to pass with the navigation.
     */
    const navigateBack = useCallback(
      ({
        fallbackPath,
        state,
        ignorePreviousUrlQueryParams,
      }: INavigateBackProps) => {
        navigateBackRef.current({
          fallbackPath,
          state,
          ignorePreviousUrlQueryParams,
        })
      },
      []
    )

    /**
     * Changes the URL search parameters.
     * @param params
     *   params: The search parameters to set (object or URLSearchParams).
     *   replace: If true, replaces the current history entry instead of adding a new one.
     */
    const changeUrlSearchParams = useCallback(
      ({ params, replace, preserveHash }: IChangeUrlSearchParamsProps) => {
        const options: NavigateOptions = {}

        if (replace) {
          popHistory()
          options.replace = true
        }

        // Save hash if we need to preserve it
        const savedHash = preserveHash ? location.hash : ''

        setSearchParams(
          params,
          ...(Object.keys(options).length > 0 ? [options] : [])
        )

        // Restore hash if it was requested and got lost
        if (preserveHash && savedHash && window.location.hash !== savedHash) {
          window.location.hash = savedHash
        }
      },
      [popHistory, setSearchParams, location.hash]
    )

    /**
     * Changes the entire URL path using direct browser history manipulation.
     * @param params
     *   newPath: The new path to set in the browser URL.
     *   replace: If true (default), replaces the last entry in the navigation history. If false, does not update history.
     */
    const changeUrlPath = useCallback(
      ({ newPath, replace = true }: IChangeUrlPathProps) => {
        if (replace) {
          const changeLastPathToNewPath = () =>
            setHistory((currentHistory) => {
              if (
                !shouldUpdatePath({ newPath, currentHistory, replace: true })
              ) {
                return currentHistory.slice(0, -1)
              }

              const newHistory = [...currentHistory]
              newHistory[newHistory.length - 1] = newPath

              return newHistory
            })

          // this is important to wait for the history to be updated before the new path is set
          setTimeout(changeLastPathToNewPath, 0)
        }

        UrlUtils.changeUrlPath({ newPath })
      },
      [shouldUpdatePath]
    )

    /**
     * Changes only the URL hash while preserving pathname and search params.
     * @param params
     *   newHash: The new hash to set (without the # symbol).
     *   replace: If true (default), replaces the last entry in the navigation history. If false, does not update history.
     */
    const changeHash = useCallback(
      ({ newHash, replace = true }: IChangeHashProps) => {
        const { pathname, search } = location
        const newPath = `${pathname}${search}#${newHash}`

        if (replace) {
          const changeLastPathToNewPath = () =>
            setHistory((currentHistory) => {
              if (
                !shouldUpdatePath({ newPath, currentHistory, replace: true })
              ) {
                return currentHistory.slice(0, -1)
              }

              const newHistory = [...currentHistory]
              newHistory[newHistory.length - 1] = newPath

              return newHistory
            })

          // this is important to wait for the history to be updated before the new path is set
          setTimeout(changeLastPathToNewPath, 0)
        }

        // Use direct browser history manipulation, can't use navigate because it will trigger a new history entry
        UrlUtils.changeUrlPath({ newPath })
      },
      [location, shouldUpdatePath]
    )

    return (
      <NavigationContext.Provider
        value={{
          history,
          navigateTo,
          navigateBack,
          changeUrlSearchParams,
          changeUrlPath,
          changeHash,
        }}
      >
        {children}
      </NavigationContext.Provider>
    )
  }
)

NavigationProvider.displayName = 'NavigationProvider'

export { NavigationProvider }
